import AnimateOnScroll from './AnimateOnScroll';
type CellValue = 'yes' | 'partial' | 'no';

interface ComparisonRow {
  feature: string;
  envizage: CellValue;
  traditional: CellValue;
  traditionalNote?: string;
  generic: CellValue;
  genericNote?: string;
}

interface ComparisonGroup {
  title: string;
  icon: string;
  rows: ComparisonRow[];
}

const groups: ComparisonGroup[] = [
  {
    title: 'Planning & Accuracy',
    icon: 'ri-bar-chart-2-line',
    rows: [
      { feature: 'CFP grade holistic forecasts', envizage: 'yes', traditional: 'yes', generic: 'no' },
      { feature: 'Accurate AND realistic goal achievability', envizage: 'yes', traditional: 'no', generic: 'no' },
      { feature: 'Planner-validated investment propositions', envizage: 'yes', traditional: 'yes', generic: 'no' },
    ],
  },
  {
    title: 'Compliance & Security',
    icon: 'ri-shield-check-line',
    rows: [
      { feature: 'FCA-aligned processes & Consumer Duty support', envizage: 'yes', traditional: 'yes', generic: 'no' },
      { feature: 'Repeatable forecasts which meet FCA requirements', envizage: 'yes', traditional: 'yes', generic: 'no' },
      { feature: 'Full compliance audit trail', envizage: 'yes', traditional: 'yes', generic: 'no' },
      { feature: 'UK-hosted, GDPR-compliant data storage', envizage: 'yes', traditional: 'partial', generic: 'no' },
    ],
  },
  {
    title: 'Client Experience',
    icon: 'ri-user-smile-line',
    rows: [
      { feature: 'Conversational AI for client data input', envizage: 'yes', traditional: 'no', generic: 'yes' },
      { feature: 'Simplicity and familiarity of customer interface', envizage: 'yes', traditional: 'no', generic: 'yes' },
      { feature: 'Conversational AI for explanation of cash flow plan', envizage: 'yes', traditional: 'no', generic: 'yes' },
      { feature: 'Conversational "Next Best Actions" for cash flow plan', envizage: 'yes', traditional: 'no', generic: 'yes' },
      { feature: 'Fully branded white label interface', envizage: 'yes', traditional: 'yes', generic: 'partial' },
      { feature: 'Self-service client planning portal', envizage: 'yes', traditional: 'partial', generic: 'no' },
    ],
  },
  {
    title: 'Operational Efficiency',
    icon: 'ri-flashlight-line',
    rows: [
      { feature: 'Streamlined prospecting workflow', envizage: 'yes', traditional: 'no', generic: 'yes' },
      { feature: "Automated report generation that's easily digestible by customer", envizage: 'yes', traditional: 'partial', generic: 'no' },
      { feature: 'No IT setup, infrastructure, or planner training required', envizage: 'yes', traditional: 'no', generic: 'yes' },
      { feature: 'Affordable monthly subscription', envizage: 'yes', traditional: 'no', generic: 'yes' },
    ],
  },
];

function CellIcon({ val, note }: { val: CellValue; note?: string }) {
  if (val === 'yes') return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-green-300">
        <i className="ri-check-line text-sm text-white"></i>
      </div>
    </div>
  );
  if (val === 'partial') return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-200">
        <i className="ri-subtract-line text-sm text-white"></i>
      </div>
      {note && <span className="text-[10px] text-[#9a9aaa] text-center leading-tight max-w-[80px]">{note}</span>}
    </div>
  );
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-red-300">
        <i className="ri-close-line text-sm text-white"></i>
      </div>
      {note && <span className="text-[10px] text-[#9a9aaa] text-center leading-tight max-w-[80px]">{note}</span>}
    </div>
  );
}

export default function ComparisonSection() {
  const scrollToRegister = () => {
    const el = document.querySelector('#register');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="comparison" className="bg-[#EFEBE5] py-24">
      <div className="max-w-6xl mx-auto px-6">
        <AnimateOnScroll variant="fade-up">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest text-brown-300 uppercase mb-4">Why Envizage</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a1a2e] mb-5">See How We Stack Up</h2>
            <p className="text-base text-[#6a6a7a] max-w-xl mx-auto leading-relaxed">
              Traditional planning tools are accurate but narrow in scope. Generic AI is conversational but dangerously unreliable for financial advice. Envizage combines both strengths — holistic, compliant planning with an experience clients actually understand and trust.
            </p>
          </div>
        </AnimateOnScroll>

        {/* Realism USP callout */}
        {/* <AnimateOnScroll variant="fade-up" delay={80}>
          <div className="mb-10 rounded border border-[#D8C9B0] bg-gradient-to-r from-[#FAF5EB] to-[#EFEBE5] p-7 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="flex items-center justify-center w-16 h-16 rounded bg-[#1BB184] flex-shrink-0">
              <i className="ri-scales-3-line text-2xl text-white"></i>
            </div>
            <div>
              <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                <h3 className="text-base font-bold text-[#1a1a2e]">Correct plans aren&apos;t enough. Ours are also realistic.</h3>
                <span className="text-[10px] font-bold bg-[#1BB184] text-white px-2.5 py-1 rounded whitespace-nowrap uppercase tracking-wide">Key Differentiator</span>
              </div>
              <p className="text-md text-[#5a5a6a] leading-relaxed">
                Any good planning tool can run the numbers correctly. The problem is that technically correct plans often prescribe behaviours clients simply won&apos;t — or can&apos;t — follow. Envizage builds plans grounded in real-world achievability, so when a client sees their forecast, they believe it and act on it. That&apos;s rare in this market. No competitor below can say the same.
              </p>
            </div>
          </div>
        </AnimateOnScroll> */}

        <AnimateOnScroll variant="fade-up" delay={100}>
          <div className="bg-white rounded-2xl shadow-lg overflow-x-auto">
            <div className="min-w-[640px]">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_140px_140px_140px] lg:grid-cols-[1fr_180px_180px_180px]">
              <div className="px-7 py-5 border-b border-r border-[#EDE8DF]">
                <span className="text-xs font-bold text-brown-100 uppercase tracking-widest">Feature</span>
              </div>
              <div className="bg-brown-100 px-5 py-5 flex flex-col items-center gap-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-md font-bold text-white whitespace-nowrap">Envizage.ai</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-300">
                  <i className="ri-check-line text-xs text-white"></i>
                </div>
              </div>
              <div className="bg-[#FFFFFF] px-5 py-5 border-b border-r border-[#EDE8DF] flex flex-col items-center gap-1.5">
                <span className="text-xs font-bold text-[#4a4a4a] text-center leading-tight whitespace-nowrap">Traditional Tools</span>
              </div>
              <div className="bg-[#FFFFFF] px-5 py-5 border-b border-[#EDE8DF] flex flex-col items-center gap-1.5">
                <span className="text-xs font-bold text-[#4a4a4a] text-center leading-tight whitespace-nowrap">Generic AI</span>
              </div>
            </div>

            {groups.map((g) => (
              <div key={g.title}>
                <div className="grid grid-cols-[1fr_140px_140px_140px] lg:grid-cols-[1fr_180px_180px_180px] bg-[#FDFAF6] border-b border-[#EDE8DF]">
                  <div className="px-7 py-3 flex items-center gap-2.5 col-span-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-[#E8F5EE]">
                      <i className={`${g.icon} text-xs text-[#1BB184]`}></i>
                    </div>
                    <span className="text-xs font-bold text-[#1a1a2e] uppercase tracking-widest">{g.title}</span>
                  </div>
                </div>
                {g.rows.map((row) => (
                  <div key={row.feature} className="grid grid-cols-[1fr_140px_140px_140px] lg:grid-cols-[1fr_180px_180px_180px] border-b border-[#EDE8DF] hover:bg-[#FDFAF6] transition-colors">
                    <div className="px-7 py-4 flex items-center border-r border-[#EDE8DF]">
                      <span className="text-md text-[#3a3a4a] leading-snug">{row.feature}</span>
                    </div>
                    <div className="px-4 py-4 flex items-start justify-center border-r border-[#EDE8DF] bg-[#E8F5EE]/20">
                      <CellIcon val={row.envizage} />
                    </div>
                    <div className="px-4 py-4 flex items-start justify-center border-r border-[#EDE8DF]">
                      <CellIcon val={row.traditional} note={row.traditionalNote} />
                    </div>
                    <div className="px-4 py-4 flex items-start justify-center">
                      <CellIcon val={row.generic} note={row.genericNote} />
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="grid grid-cols-[1fr_140px_140px_140px] lg:grid-cols-[1fr_180px_180px_180px] bg-[#FDFAF6] border-t-2 border-[#EDE8DF]">
              <div className="px-7 py-5 flex items-center border-r border-[#EDE8DF]">
                <span className="text-md font-bold text-[#1a1a2e]">Overall Score</span>
              </div>
              <div className="bg-brown-200 px-4 py-5 flex flex-col items-center gap-1">
                <span className="text-xl font-extrabold text-white">17/17</span>
                <span className="text-[10px] text-white/60 whitespace-nowrap">Full coverage</span>
              </div>
              <div className="px-4 py-5 flex flex-col items-center gap-1 border-r border-[#EDE8DF]">
                <span className="text-xl font-extrabold text-[#1a1a2e]">6/17</span>
                <span className="text-[10px] text-[#9a9aaa] whitespace-nowrap">Partial/limited</span>
              </div>
              <div className="px-4 py-5 flex flex-col items-center gap-1">
                <span className="text-xl font-extrabold text-[#1a1a2e]">7/17</span>
                <span className="text-[10px] text-[#9a9aaa] whitespace-nowrap">Mostly absent</span>
              </div>
            </div>
            </div>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll variant="fade-up" delay={80}>
          <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
            {[
              { icon: 'ri-check-line', bg: 'bg-green-300', color: 'text-white', label: 'Fully supported' },
              { icon: 'ri-subtract-line', bg: 'bg-orange-200', color: 'text-white', label: 'Partially supported' },
              { icon: 'ri-close-line', bg: 'bg-red-300', color: 'text-white', label: 'Not supported' },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-2">
                <div className={`w-6 h-6 flex items-center justify-center rounded-full ${l.bg}`}>
                  <i className={`${l.icon} text-xs ${l.color}`}></i>
                </div>
                <span className="text-xs text-[#6a6a7a] whitespace-nowrap">{l.label}</span>
              </div>
            ))}
          </div>
        </AnimateOnScroll>

        {/* <AnimateOnScroll variant="fade-up" delay={120}>
          <div className="mt-12 text-center">
            <button
              onClick={scrollToRegister}
              className="inline-flex items-center gap-2 bg-[#1BB184] text-white text-md font-semibold px-8 py-3 rounded hover:bg-[#004a32] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-rocket-line"></i>Get Started
            </button>
          </div>
        </AnimateOnScroll> */}
      </div>
    </section>
  );
}
